import React, { useEffect, useState } from 'react';
import FurnitureNavbar from '../../../components/layout/Nav';
import './projectrequests.scss';
import withAuth from '../../auth/WithAuth';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { serverUrlAuth } from '../../../serverUrl';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import {
  FaUserCircle,
  FaCalendar,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaClock,
  FaCheckCircle,
  FaEye,
  FaEnvelope,
  FaFileAlt,
  FaComments,
  FaWhatsapp,
  FaBriefcase,
  FaCheck,
  FaSpinner,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaFilter,
} from 'react-icons/fa';

const ProjectRequests = () => {
  const navigate = useNavigate();

  const [matchedProjects, setMatchedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [showProposalModal, setShowProposalModal] = useState(false);
  const [selectedMatchId, setSelectedMatchId] = useState(null);
  const [showProposalDetailsModal, setShowProposalDetailsModal] = useState(false);
  const [submittedProposal, setSubmittedProposal] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'new', 'proposed', 'in-progress'

  // Proposal states
  const [proposalData, setProposalData] = useState({
    amount: '',
    timeline: '',
    message: '',
  });

  // ✅ Fetch matched projects (including in-progress projects)
  const fetchMatchedProjects = async () => {
    setLoading(true);
    try {
      const response = await serverUrlAuth.get(
        'projects/artisan/matched-projects/',
      );
      console.log('Matched projects:', response.data);
      
      // Process projects to add hasProposal flag
      const processedProjects = await Promise.all(
        response.data.map(async (project) => {
          const hasProposal = await checkIfProposalExists(project.project);
          return { ...project, hasProposal };
        })
      );
      
      setMatchedProjects(processedProjects);
    } catch (error) {
      console.error('Error fetching matched projects:', error);
      toast.error('Failed to fetch matched projects');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Check if proposal already exists for this project
  const checkIfProposalExists = async (projectId) => {
    try {
      const response = await serverUrlAuth.get(`proposals/proposals-list/${projectId}/`);
      return response.data.length > 0;
    } catch (error) {
      console.error('Error checking proposal:', error);
      return false;
    }
  };

  // ✅ Mark match as viewed
  const markMatchAsViewed = async (matchId) => {
    try {
      await serverUrlAuth.post(`artisan-matches/matches/${matchId}/view/`);
      setMatchedProjects((prev) =>
        prev.map((match) =>
          match.id === matchId ? { ...match, status: 'viewed' } : match,
        ),
      );
    } catch (error) {
      console.error('Error marking match as viewed:', error);
    }
  };

  // ✅ Open proposal modal
  const handleSubmitProposalClick = (match) => {
    setSelectedMatchId(match.id);
    setSelectedRequest(match);
    setShowProposalModal(true);
  };

  // ✅ Submit proposal
  const handleSubmitProposal = async (e) => {
    e.preventDefault();
    if (!selectedMatchId || !selectedRequest) return;

    try {
      const formData = new FormData();
      formData.append('project', selectedRequest.project);
      formData.append('proposed_price', proposalData.amount);
      formData.append('proposed_date_of_completion', proposalData.timeline);
      formData.append('note', proposalData.message);

      await serverUrlAuth.post('proposals/create-proposal/', formData);

      // Update the project to mark as proposed and hasProposal
      setMatchedProjects((prev) =>
        prev.map((match) =>
          match.id === selectedMatchId
            ? { ...match, status: 'proposed', hasProposal: true }
            : match,
        ),
      );

      toast.success(
        'Proposal submitted successfully! The client will review it.',
      );
      setShowProposalModal(false);
      setProposalData({ amount: '', timeline: '', message: '' });
      setSelectedMatchId(null);
      setSelectedRequest(null);
      
      // Refresh to ensure UI updates
      fetchMatchedProjects();
    } catch (error) {
      console.error('Error submitting proposal:', error);
      const errMsg =
        error.response?.data?.detail || 'Error submitting proposal';
      toast.error(errMsg);
    }
  };

  // ✅ Reject match
  const handleRejectMatch = async (matchId) => {
    if (window.confirm('Are you sure you want to decline this project?')) {
      try {
        await serverUrlAuth.post(
          `artisan-matches/matches/${matchId}/respond/`,
          {
            action: 'reject',
          },
        );
        setMatchedProjects((prev) =>
          prev.map((match) =>
            match.id === matchId ? { ...match, status: 'rejected' } : match,
          ),
        );
        toast.info('You have declined this project');
        
        // Refresh to remove declined projects
        fetchMatchedProjects();
      } catch (error) {
        console.error('Error rejecting match:', error);
        toast.error('Failed to decline project');
      }
    }
  };

  // ✅ View proposal details
  const viewProposal = async (match) => {
    try {
      const proposalsRes = await serverUrlAuth.get(
        `proposals/proposals-list/${match.project}/`,
      );
      if (proposalsRes.data.length > 0) {
        const proposal = proposalsRes.data[0];
        setSubmittedProposal({
          id: proposal.id,
          amount: proposal.proposed_price,
          timeline: proposal.proposed_date_of_completion,
          message: proposal.note,
          status: proposal.status,
          submitted_at: proposal.created_at,
          is_selected: proposal.is_selected,
        });
        setSelectedRequest(match);
        setShowProposalDetailsModal(true);
      }
    } catch (error) {
      console.error('Error fetching proposal:', error);
      toast.error('Failed to load proposal details');
    }
  };

  useEffect(() => {
    fetchMatchedProjects();
  }, []);

  // Helper functions
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'open':
        return 'status-pending';
      case 'pending':
        return 'status-pending';
      case 'viewed':
        return 'status-viewed';
      case 'proposed':
        return 'status-proposed';
      case 'rejected':
        return 'status-rejected';
      case 'accepted':
        return 'status-accepted';
      case 'in-progress':
        return 'status-in-progress';
      default:
        return 'status-pending';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open':
        return <FaClock />;
      case 'pending':
        return <FaClock />;
      case 'viewed':
        return <FaEye />;
      case 'proposed':
        return <FaFileAlt />;
      case 'rejected':
        return '❌';
      case 'accepted':
        return <FaCheckCircle />;
      case 'in-progress':
        return <FaSpinner />;
      default:
        return <FaClock />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'open':
        return 'New Match';
      case 'pending':
        return 'New Match';
      case 'viewed':
        return 'Viewed';
      case 'proposed':
        return 'Proposal Sent';
      case 'rejected':
        return 'Declined';
      case 'accepted':
        return 'Proposal Accepted';
      case 'in-progress':
        return 'Project In Progress';
      default:
        return status;
    }
  };

  // Filter projects based on active filter and status
  const getFilteredProjects = () => {
    switch (activeFilter) {
      case 'new':
        return matchedProjects.filter(
          (m) => 
            (m.status === 'open' || m.status === 'pending' || m.status === 'viewed') && 
            !m.hasProposal &&
            m.status !== 'rejected'
        );
      case 'proposed':
        return matchedProjects.filter(
          (m) => 
            (m.status === 'proposed' || m.hasProposal === true) && 
            m.status !== 'rejected' &&
            m.status !== 'accepted' &&
            m.status !== 'in-progress'
        );
      case 'in-progress':
        return matchedProjects.filter(
          (m) => 
            (m.status === 'accepted' || m.status === 'in-progress') && 
            m.status !== 'rejected'
        );
      default:
        return matchedProjects.filter(m => m.status !== 'rejected');
    }
  };

  const filteredProjects = getFilteredProjects();
  
  // Get counts for filters
  const newMatchesCount = matchedProjects.filter(
    (m) => 
      (m.status === 'open' || m.status === 'pending' || m.status === 'viewed') && 
      !m.hasProposal &&
      m.status !== 'rejected'
  ).length;
  
  const proposedCount = matchedProjects.filter(
    (m) => 
      (m.status === 'proposed' || m.hasProposal === true) && 
      m.status !== 'rejected' &&
      m.status !== 'accepted' &&
      m.status !== 'in-progress'
  ).length;
  
  const inProgressCount = matchedProjects.filter(
    (m) => 
      (m.status === 'accepted' || m.status === 'in-progress') && 
      m.status !== 'rejected'
  ).length;

  const getProposalStatusBadge = (status, isSelected) => {
    if (isSelected) return 'status-accepted';
    switch (status) {
      case 'pending':
        return 'status-pending-proposal';
      case 'Accepted':
        return 'status-accepted';
      case 'Declined':
        return 'status-declined';
      default:
        return 'status-pending-proposal';
    }
  };

  const getProposalStatusText = (status, isSelected) => {
    if (isSelected) return 'Selected ✓ - Project In Progress';
    switch (status) {
      case 'pending':
        return 'Pending Review';
      case 'Accepted':
        return 'Accepted ✓';
      case 'Declined':
        return 'Declined';
      default:
        return status;
    }
  };

  // Image carousel navigation
  const nextImage = () => {
    if (selectedRequest?.images?.length) {
      setActiveImage((prev) => (prev + 1) % selectedRequest.images.length);
    }
  };

  const prevImage = () => {
    if (selectedRequest?.images?.length) {
      setActiveImage((prev) =>
        prev === 0 ? selectedRequest.images.length - 1 : prev - 1,
      );
    }
  };

  return (
    <>
      <FurnitureNavbar />

      <div className="browse-requests-page">
        {/* Hero Section */}
        <section className="hero-banner">
          <div className="hero-overlay">
            <div className="hero-content">
              <h1>🎯 My Projects</h1>
              <p>Track your matched projects, proposals, and ongoing work.</p>
            </div>
          </div>
        </section>


        {/* Filter Tabs */}
        <div className="filter-tabs">
          <button
            className={`filter-tab ${activeFilter === 'all' ? 'active' : ''}`}
            onClick={() => setActiveFilter('all')}
          >
            All Projects ({matchedProjects.filter(m => m.status !== 'rejected').length})
          </button>
          <button
            className={`filter-tab ${activeFilter === 'new' ? 'active' : ''}`}
            onClick={() => setActiveFilter('new')}
          >
            <FaClock /> New Matches ({newMatchesCount})
          </button>
          <button
            className={`filter-tab ${activeFilter === 'proposed' ? 'active' : ''}`}
            onClick={() => setActiveFilter('proposed')}
          >
            <FaFileAlt /> Proposals Sent ({proposedCount})
          </button>
          <button
            className={`filter-tab ${activeFilter === 'in-progress' ? 'active' : ''}`}
            onClick={() => setActiveFilter('in-progress')}
          >
            <FaSpinner /> In Progress ({inProgressCount})
          </button>
        </div>

        {/* Projects Grid */}
        {filteredProjects.length > 0 ? (
          <div className="projects-section">
            <div className="requests-grid">
              {filteredProjects.map((match) => {
                const isProposalSubmitted = match.hasProposal || match.status === 'proposed';
                const isInProgress = match.status === 'accepted' || match.status === 'in-progress';
                const isNewMatch = (match.status === 'open' || match.status === 'pending' || match.status === 'viewed') && !isProposalSubmitted;
                
                return (
                  <div key={match.id} className={`request-card ${isInProgress ? 'in-progress-card' : ''}`}>
                    <div className="request-image">
                      <img
                        src={
                          match.images && match.images.length > 0
                            ? match.images[0].image
                            : 'https://via.placeholder.com/400x300?text=No+Image'
                        }
                        alt={match.project_title}
                        onError={(e) => {
                          e.target.src =
                            'https://via.placeholder.com/400x300?text=No+Image';
                          e.target.onerror = null;
                        }}
                      />
                      <div className="budget-badge">
                        <FaMoneyBillWave /> KSh{' '}
                        {/* {match.status === 'in-progress' || match.status === "completed" ? parseFloat(match.agreed_cost).toLocaleString() : parseFloat(match.budget).toLocaleString()} */}
                        {match.status === 'open'? parseFloat(match.budget).toLocaleString() : parseFloat(match.agreed_cost).toLocaleString()}
                      </div>
                      <div
                        className={`status-badge ${getStatusBadgeClass(match.status)}`}
                      >
                        {getStatusIcon(match.status)}{' '}
                        {getStatusText(match.status)}
                      </div>
                    </div>
                    <div className="request-content">
                      <h3>{match.project_title}</h3>
                      <div className="request-meta">
                        <div className="meta-item">
                          <FaUserCircle className="meta-icon" />
                          <span>Client: {match.client_name || 'Anonymous'}</span>
                        </div>
                        <div className="meta-item">
                          <FaMapMarkerAlt className="meta-icon" />
                          <span>
                            {match.location || 'Location not specified'}
                          </span>
                        </div>
                        <div className="meta-item">
                          <FaCalendar className="deadline-icon" />
                          <div className="deadline-text">
                          Deadline: {new Date(match.deadline).toLocaleDateString()}
                        </div>
                        </div>
                      </div>
                      <div className="deadline">
                        {/* <FaCalendar className="deadline-icon" /> */}
                        <div className="deadline-text">
                          Description: {match.description}
                        </div>
                      </div>
                      <div className="card-actions">
                        <button
                          className="view-btn"
                          onClick={() => {
                            if (match.status === 'pending' || match.status === 'open') {
                              markMatchAsViewed(match.id);
                            }
                            setSelectedRequest(match);
                            setActiveImage(0);
                          }}
                        >
                          <FaEye /> View Project
                        </button>
                        
                        {/* Show Submit Proposal and Decline buttons ONLY for new matches without proposals */}
                        {isNewMatch && !isProposalSubmitted && (
                          <>
                            <button
                              className="proposal-btn"
                              onClick={() => handleSubmitProposalClick(match)}
                            >
                              <FaFileAlt /> Submit Proposal
                            </button>
                            <button
                              className="decline-btn"
                              onClick={() => handleRejectMatch(match.id)}
                            >
                              Decline
                            </button>
                          </>
                        )}
                        
                        {/* Show View Proposal button for projects with submitted proposals */}
                        {isProposalSubmitted && !isInProgress && (
                          <button
                            className="view-proposal-btn"
                            onClick={() => viewProposal(match)}
                          >
                            <FaFileAlt /> View Proposal
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">
              {activeFilter === 'new' ? '🎯' : activeFilter === 'proposed' ? '📝' : activeFilter === 'in-progress' ? '⚙️' : '🔍'}
            </div>
            <h3>
              {activeFilter === 'new' && 'No new matches yet'}
              {activeFilter === 'proposed' && 'No proposals sent yet'}
              {activeFilter === 'in-progress' && 'No projects in progress'}
              {activeFilter === 'all' && 'No projects found'}
            </h3>
            <p>
              {activeFilter === 'new' && 'When clients post projects that match your skills, they\'ll appear here.'}
              {activeFilter === 'proposed' && 'Your submitted proposals will appear here once you send them.'}
              {activeFilter === 'in-progress' && 'Accepted projects will appear here.'}
              {activeFilter === 'all' && 'No projects match your criteria.'}
            </p>
          </div>
        )}

        {loading && (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading your projects...</p>
          </div>
        )}
      </div>

      {/* Submit Proposal Modal - Image Carousel Left, Form Right */}
      <Modal
        show={showProposalModal}
        onHide={() => {
          setShowProposalModal(false);
          setSelectedRequest(null);
          setSelectedMatchId(null);
          setProposalData({ amount: '', timeline: '', message: '' });
          setActiveImage(0);
        }}
        size="xl"
        centered
        className="proposal-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Submit Your Proposal</Modal.Title>
        </Modal.Header>
        <Modal.Body className="proposal-modal-body">
          {selectedRequest && (
            <div className="proposal-two-column">
              {/* Left Column - Image Carousel */}
              <div className="proposal-image-section">
                <div className="image-carousel-container">
                  {selectedRequest.images && selectedRequest.images.length > 0 ? (
                    <>
                      <div className="carousel-main-image">
                        <img
                          src={selectedRequest.images[activeImage]?.image}
                          alt={selectedRequest.project_title}
                        />
                        {selectedRequest.images.length > 1 && (
                          <>
                            <button
                              className="carousel-nav-btn prev"
                              onClick={prevImage}
                            >
                              <FaChevronLeft />
                            </button>
                            <button
                              className="carousel-nav-btn next"
                              onClick={nextImage}
                            >
                              <FaChevronRight />
                            </button>
                          </>
                        )}
                      </div>
                      {selectedRequest.images.length > 1 && (
                        <div className="carousel-thumbnails">
                          {selectedRequest.images.map((img, idx) => (
                            <img
                              key={img.id}
                              src={img.image}
                              alt={`Thumb ${idx + 1}`}
                              className={
                                activeImage === idx ? 'active-thumb' : ''
                              }
                              onClick={() => setActiveImage(idx)}
                            />
                          ))}
                        </div>
                      )}
                      <div className="image-counter">
                        {activeImage + 1} / {selectedRequest.images.length}
                      </div>
                    </>
                  ) : (
                    <div className="no-image-placeholder">
                      <img
                        src="https://via.placeholder.com/500x400?text=No+Image"
                        alt="Not Available"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column - Proposal Form */}
              <div className="proposal-form-section">
                <div className="project-info-card">
                  <h3>{selectedRequest.project_title}</h3>
                  <div className="project-details">
                    <div className="detail-item">
                      <FaUserCircle className="detail-icon" />
                      <span>
                        <strong>Client:</strong> {selectedRequest.client_name}
                      </span>
                    </div>
                    <div className="detail-item">
                      <FaMoneyBillWave className="detail-icon" />
                      <span>
                        <strong>Budget:</strong> KSh{' '}
                        {parseFloat(selectedRequest.budget).toLocaleString()}
                      </span>
                    </div>
                    <div className="detail-item">
                      <FaCalendar className="detail-icon" />
                      <span>
                        <strong>Deadline:</strong>{' '}
                        {new Date(selectedRequest.deadline).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="detail-item">
                      <FaMapMarkerAlt className="detail-icon" />
                      <span>
                        <strong>Location:</strong>{' '}
                        {selectedRequest.location || 'Not specified'}
                      </span>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmitProposal}>
                  <div className="form-group">
                    <label>Proposed Amount (KSh) <span className="required">*</span></label>
                    <input
                      type="number"
                      name="amount"
                      value={proposalData.amount}
                      onChange={(e) =>
                        setProposalData({ ...proposalData, amount: e.target.value })
                      }
                      placeholder={`e.g., ${parseFloat(selectedRequest.budget).toLocaleString()}`}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Proposed Completion Date <span className="required">*</span></label>
                    <input
                      type="date"
                      name="timeline"
                      min={new Date().toISOString().split('T')[0]}
                      value={proposalData.timeline}
                      onChange={(e) =>
                        setProposalData({
                          ...proposalData,
                          timeline: e.target.value,
                        })
                      }
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Message to Client <span className="required">*</span></label>
                    <textarea
                      name="message"
                      rows="4"
                      value={proposalData.message}
                      onChange={(e) =>
                        setProposalData({
                          ...proposalData,
                          message: e.target.value,
                        })
                      }
                      placeholder="Introduce yourself and explain why you're the best fit for this project..."
                      required
                    />
                  </div>

                  <button className="submit-proposal-btn" type="submit">
                    <FaFileAlt /> Submit Proposal
                  </button>
                </form>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowProposalModal(false);
              setSelectedRequest(null);
              setSelectedMatchId(null);
              setProposalData({ amount: '', timeline: '', message: '' });
              setActiveImage(0);
            }}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Project Details Modal */}
      <Modal
        show={
          !!selectedRequest && !showProposalModal && !showProposalDetailsModal
        }
        onHide={() => {
          setSelectedRequest(null);
          setActiveImage(0);
        }}
        size="lg"
        centered
        className="request-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{selectedRequest?.project_title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && (
            <div className="modal-flex">
              {selectedRequest.images && selectedRequest.images.length > 0 && (
                <div className="modal-carousel">
                  <img
                    src={selectedRequest.images[activeImage]?.image}
                    alt={`${selectedRequest.project_title} - ${activeImage + 1}`}
                    className="main-image"
                    onError={(e) => {
                      e.target.src =
                        'https://via.placeholder.com/400x300?text=No+Image';
                    }}
                  />
                  {selectedRequest.images.length > 1 && (
                    <div className="carousel-thumbnails">
                      {selectedRequest.images.map((img, idx) => (
                        <img
                          key={img.id}
                          src={img.image}
                          alt={`Thumb ${idx + 1}`}
                          className={activeImage === idx ? 'active-thumb' : ''}
                          onClick={() => setActiveImage(idx)}
                          onError={(e) => {
                            e.target.src =
                              'https://via.placeholder.com/100x100?text=No+Image';
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="modal-details">
                <p>
                  <strong>Client:</strong>{' '}
                  {selectedRequest.client_name || 'Anonymous'}
                </p>
                <p>
                  <strong>Budget:</strong> KSh{' '}
                  {parseFloat(selectedRequest.budget).toLocaleString()}
                </p>
                <p>
                  <strong>Deadline:</strong>{' '}
                  {new Date(selectedRequest.deadline).toLocaleDateString()}
                </p>
                <p>
                  <strong>Description:</strong>{' '}
                  {selectedRequest.description ||
                    selectedRequest.project_description}
                </p>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setSelectedRequest(null);
              setActiveImage(0);
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Proposal Details Modal */}
      <Modal
        show={showProposalDetailsModal}
        onHide={() => {
          setShowProposalDetailsModal(false);
          setSubmittedProposal(null);
        }}
        size="lg"
        centered
        className="proposal-details-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Your Proposal Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {submittedProposal && (
            <div className="proposal-details-content">
              <div
                className={`proposal-status-badge ${getProposalStatusBadge(submittedProposal.status, submittedProposal.is_selected)}`}
              >
                {getProposalStatusText(
                  submittedProposal.status,
                  submittedProposal.is_selected,
                )}
              </div>
              <div className="proposal-info">
                <div className="info-row">
                  <strong>Proposed Amount:</strong>
                  <span>
                    KSh {parseFloat(submittedProposal.amount).toLocaleString()}
                  </span>
                </div>
                <div className="info-row">
                  <strong>Completion Date:</strong>
                  <span>
                    {new Date(submittedProposal.timeline).toLocaleDateString()}
                  </span>
                </div>
                <div className="info-row">
                  <strong>Submitted On:</strong>
                  <span>
                    {new Date(submittedProposal.submitted_at).toLocaleString()}
                  </span>
                </div>
                <div className="info-row message-row">
                  <strong>Your Message:</strong>
                  <p>{submittedProposal.message}</p>
                </div>
              </div>
              {submittedProposal.is_selected ? (
                <div className="proposal-note accepted">
                  <p>
                    ✅ Congratulations! Your proposal has been selected. Project
                    is now in progress.
                  </p>
                  <button
                    className="chat-now-btn"
                    onClick={() => {
                      setShowProposalDetailsModal(false);
                      navigate('/messages');
                    }}
                  >
                    <FaComments /> Go to Messages
                  </button>
                </div>
              ) : (
                submittedProposal.status === 'pending' && (
                  <div className="proposal-note">
                    <p>
                      ⏳ Your proposal is pending client review. You will be
                      notified once they respond.
                    </p>
                  </div>
                )
              )}
              {submittedProposal.status === 'Declined' && (
                <div className="proposal-note declined">
                  <p>❌ Your proposal was declined by the client.</p>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowProposalDetailsModal(false);
              setSubmittedProposal(null);
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withAuth(ProjectRequests, 'artisan');